/**
 * TrackerX calendar-date helpers.
 *
 * Tracker entry dates follow the calendar-date convention: the server stores
 * the user's picked day at UTC midnight (e.g. "2026-06-07T00:00:00.000Z").
 * Reading the day back must therefore use the UTC date component — converting
 * the stored ISO through browser-local time (format(new Date(iso), ...))
 * would show/edit the PREVIOUS day for users west of UTC.
 */
import {
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from "date-fns";
import { formatDateInput } from "./dateUtils";

/**
 * UTC calendar day (YYYY-MM-DD) of a stored entry date.
 * Accepts the ISO string as stored by the server or an already-parsed Date.
 * Falls back to the raw string when it isn't a parseable ISO-like date.
 *
 * This is THE canonical helper for reading a tracker/habit entry's day back
 * out — never hand-roll `new Date(iso)` local formatting or
 * `toISOString().split("T")[0]` in screen code (a vitest guard enforces this).
 */
export function utcEntryDay(iso: string | Date): string {
  if (iso instanceof Date) {
    return isNaN(iso.getTime()) ? String(iso) : iso.toISOString().slice(0, 10);
  }
  const s = String(iso);
  return /^\d{4}-\d{2}-\d{2}/.test(s) && !isNaN(new Date(s).getTime())
    ? s.slice(0, 10)
    : s;
}

/** UTC-midnight instant (ISO string) for a local calendar day string. */
export function calendarDayUtcStart(day: string): string {
  return `${day}T00:00:00.000Z`;
}

/** End-of-day UTC instant (ISO string) for a local calendar day string. */
export function calendarDayUtcEnd(day: string): string {
  return `${day}T23:59:59.999Z`;
}

export type RangePreset =
  | "all"
  | "day"
  | "week"
  | "month"
  | "quarter"
  | "year"
  | "custom";

/**
 * Compute the entry-date query range for a preset.
 *
 * The user's "today"/"this week" is their LOCAL calendar period, but entries
 * are stored at UTC midnight of the picked calendar day. So we resolve the
 * local calendar boundary days first (via local date math), then convert
 * those day strings to UTC-midnight bounds. Building bounds with
 * startOfDay(now).toISOString() (a local instant) would exclude today's
 * UTC-midnight entry for users west of UTC.
 */
export function getRangeDates(
  preset: RangePreset,
  customFrom: string,
  customTo: string,
): { from?: string; to?: string } {
  const now = new Date();
  const today = formatDateInput(now);
  switch (preset) {
    case "all":
      return {};
    case "day":
      return { from: calendarDayUtcStart(today), to: calendarDayUtcEnd(today) };
    case "week":
      return {
        from: calendarDayUtcStart(formatDateInput(startOfWeek(now, { weekStartsOn: 1 }))),
        to: calendarDayUtcEnd(today),
      };
    case "month":
      return {
        from: calendarDayUtcStart(formatDateInput(startOfMonth(now))),
        to: calendarDayUtcEnd(today),
      };
    case "quarter":
      return {
        from: calendarDayUtcStart(formatDateInput(startOfQuarter(now))),
        to: calendarDayUtcEnd(today),
      };
    case "year":
      return {
        from: calendarDayUtcStart(formatDateInput(startOfYear(now))),
        to: calendarDayUtcEnd(today),
      };
    case "custom": {
      const r: { from?: string; to?: string } = {};
      if (customFrom) r.from = calendarDayUtcStart(customFrom);
      if (customTo) r.to = calendarDayUtcEnd(customTo);
      return r;
    }
  }
}
